import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';

import {
  BulkAction,
  BulkActionPayload,
  BulkActionResponseResultWithId,
  GetBulkActionsStatusResponse,
  GetRunningBulkActionsResponse,
  IArticleItem,
  RunningBulkAction,
  UpdateArticleGroupsPayload
} from '../types/articleItem';
import { ArticlesSearchByParams, ArticlesSortByParams, LocalStorageItems, SpotPolicy } from '../../../../../constants';
import {
  fetchArticlesFilterTagsApiCall,
  FetchArticlesFilterTagsPayload,
  fetchArticlesFilterTagsRequest,
  FetchArticlesFilterTagsResponse
} from '../api/fetchArticlesFilterTags';
import { fetchArticlesListApiCall } from '../api/fetchArticlesList';
import { setArticlesConversationPolicyApiCall } from '../api/setArticlesConversationPolicy';
import { handleApiCall } from '../../../../services/sagas/apiSagaHelpers';
import { fetchBulkActionsStatus, getRunningBulkActionsRequests, postBulkAction } from '../api/bulkActions';
import { getToastErrorMessage, getToastSuccessMessage } from '../articlesUtils';
import { parseGroups } from '../../restricted-words-next/restrictedWordsUtils';
import { setIsReadOnly } from '../api/setIsReadOnly';
import { showToast } from '../../../../../components/toast/toastSlice';
import { ToastType } from '../../../../../components/toast/toastTypes';

import {
  selectArticleRunningBulkActions,
  selectArticlesAppliedFilterTags,
  selectArticlesCurrentPage,
  selectArticlesList,
  selectArticlesPostIdsForPolling,
  selectArticlesSearchBy,
  selectArticlesSearchValue,
  selectArticlesSortBy
} from './articlesSelectors';

const EXCEED_RATE_LIMIT = 429;

export interface ILoadArticlesSliceState {
  spotPolicy: SpotPolicy;
  articles: IArticleItem[];
  articlesCount: number;
  isLoading: boolean;
  currentPage: number;
  searchBy: ArticlesSearchByParams;
  searchValue: string;
  sortBy: ArticlesSortByParams;
  filter: FilterState;
  runningBulkActions: RunningBulkAction[];
}

interface FilterState {
  tags: Array<string>;
  appliedTags: Array<string>;
  offset: number;
}

export const initialState: ILoadArticlesSliceState = {
  spotPolicy: SpotPolicy.ApproveAll,
  articles: [],
  articlesCount: 0,
  currentPage: 0,
  isLoading: true,
  searchBy:
    (localStorage.getItem(LocalStorageItems.ArticlesSearchBy) as ArticlesSearchByParams) ??
    ArticlesSearchByParams.Title,
  searchValue: localStorage.getItem(LocalStorageItems.ArticlesSearchValue) ?? '',
  sortBy:
    (localStorage.getItem(LocalStorageItems.ArticlesSortBy) as ArticlesSortByParams) ?? ArticlesSortByParams.Newest,
  filter: {
    tags: [],
    appliedTags: [],
    offset: 0
  },
  runningBulkActions: []
};

export const loadArticlesSlice = createSlice({
  initialState,
  name: 'articles',
  reducers: {
    updateFilter: (
      draft,
      action: PayloadAction<{
        tags: Array<string>;
        offset: number;
        reset: boolean;
      }>
    ) => {
      const { tags, offset, reset } = action.payload;

      if (reset) {
        draft.filter.tags = tags;
      } else {
        draft.filter.tags.push(...tags);
      }

      draft.filter.offset = offset;
    },
    changeArticles: (draft, action: PayloadAction<IArticleItem[]>) => {
      draft.articles = action.payload;
    },
    changeArticlesLoadingStatus: (draft, action: PayloadAction<boolean>) => {
      draft.isLoading = action.payload;
    },
    changeArticlesCount: (draft, action: PayloadAction<number>) => {
      draft.articlesCount = action.payload;
    },
    changeSpotPolicy: (draft, action: PayloadAction<SpotPolicy>) => {
      draft.spotPolicy = action.payload;
    },
    changeArticlesCurrentPage: (draft, action: PayloadAction<number>) => {
      draft.currentPage = action.payload;
    },
    changeArticlesSearchBy: (draft, action: PayloadAction<ArticlesSearchByParams>) => {
      localStorage.setItem(LocalStorageItems.ArticlesSearchBy, action.payload);
      draft.searchBy = action.payload;
    },
    changeArticlesSearchValue: (draft, action: PayloadAction<string>) => {
      localStorage.setItem(LocalStorageItems.ArticlesSearchValue, action.payload);
      draft.searchValue = action.payload;
      draft.currentPage = 0;
    },
    changeArticlesAppliedFilterTags: (draft, action: PayloadAction<string[]>) => {
      draft.filter.appliedTags = action.payload;
      draft.currentPage = 0;
    },
    changeArticlesSortBy: (draft, action: PayloadAction<ArticlesSortByParams>) => {
      localStorage.setItem(LocalStorageItems.ArticlesSortBy, action.payload);
      draft.sortBy = action.payload;
    },
    clearArticlesState: () => initialState,
    addRunningBulkAction: (draft, action: PayloadAction<RunningBulkAction>) => {
      draft.runningBulkActions.push(action.payload);
    },
    updateArticlesGroups: (draft, action: PayloadAction<UpdateArticleGroupsPayload>) => {
      const { postId, restrictedWordsGroups } = action.payload;
      const article = draft.articles.find(article => article.postId === postId);

      if (article) {
        article.restrictedWordsGroups = restrictedWordsGroups;
      }
    },
    updateArticleReadOnly: (
      draft,
      action: PayloadAction<{
        postId: string;
        readOnly: boolean;
      }>
    ) => {
      const { postId, readOnly } = action.payload;

      const article = draft.articles.find(article => article.postId === postId);

      if (article) {
        article.readOnly = readOnly;
      }
    },
    setRunningBulkActions: (draft, action: PayloadAction<RunningBulkAction[]>) => {
      draft.runningBulkActions = action.payload;
    },
    removeRunningBulkActions: (draft, action: PayloadAction<Array<string>>) => {
      draft.runningBulkActions = draft.runningBulkActions.filter(({ postId }) => !action.payload.includes(postId));
    }
  }
});

export const loadArticlesReducer = loadArticlesSlice.reducer;

export const {
  updateFilter,
  changeArticles,
  changeSpotPolicy,
  changeArticlesCount,
  changeArticlesLoadingStatus,
  changeArticlesCurrentPage,
  changeArticlesSearchBy,
  changeArticlesSearchValue,
  changeArticlesAppliedFilterTags,
  changeArticlesSortBy,
  clearArticlesState,
  addRunningBulkAction,
  updateArticlesGroups,
  updateArticleReadOnly,
  setRunningBulkActions,
  removeRunningBulkActions
} = loadArticlesSlice.actions;
export const initializeArticles = createAction('INITIALIZE_ARTICLES');
export const fetchArticlesFilterTags = createAction<FetchArticlesFilterTagsPayload>('FETCH_ARTICLES_FILTER_TAGS');
export const setArticlesConversationPolicy = createAction<{
  conversationId: string;
  policy: SpotPolicy;
  article_strictness: string;
  spotId: string;
  postId: string;
  username: string;
  userID: string;
}>('SET_ARTICLES_CONVERSATION_POLICY');
export const setReadOnlyAction = createAction<{
  readOnly: boolean;
  conversationId: string;
  postId: string;
}>('TOGGLE_READ_ONLY_REQUEST');
export const applyBulkAction = createAction<BulkActionPayload>('ARTICLE_BULK_ACTION');
export const startBulkActionsStatusPolling = createAction('START_BULK_ACTIONS_STATUS_POLLING');
export const stopBulkActionsStatusPolling = createAction('STOP_BULK_ACTIONS_STATUS_POLLING');
export const checkRunningBulkActions = createAction('GET_RUNNING_BULK_ACTIONS');

function* loadFilterTagsSaga(action: PayloadAction<FetchArticlesFilterTagsPayload>) {
  const { payload } = action;
  const { useV2, ...fetchPayload } = payload;
  const { offset: initialOffset = 0 } = fetchPayload;
  const reset = initialOffset === 0;

  const request = useV2 ? fetchArticlesFilterTagsRequest(fetchPayload) : fetchArticlesFilterTagsApiCall();

  const {
    data
  }: {
    data: FetchArticlesFilterTagsResponse;
  } = yield call(handleApiCall, request);

  const { tags = [], offset = 0 } = data && data.tags ? data : { tags: [] };

  yield put(updateFilter({ tags, offset, reset }));
}

function* loadArticlesSaga() {
  const currentPage = yield select(selectArticlesCurrentPage);
  const searchBy = yield select(selectArticlesSearchBy);
  const searchValue = yield select(selectArticlesSearchValue);
  const appliedFilterTags = yield select(selectArticlesAppliedFilterTags);
  const sortBy = yield select(selectArticlesSortBy);

  yield put(changeArticlesLoadingStatus(true));

  const { data } = yield call(
    handleApiCall,
    fetchArticlesListApiCall({
      currentPage,
      searchBy,
      searchValue,
      appliedFilterTags,
      sortBy
    }),
    {
      ignoreErrors: true,
      shouldDispatchActions: false
    }
  );
  const { articles: articlesList = [], totalCount: articlesCount = 0, spotPolicy } = data ?? {};
  const articlesWithGroups = articlesList.map(article => {
    const { restrictedWordsGroups } = article;
    const parsedGroups = parseGroups(restrictedWordsGroups);
    return { ...article, restrictedWordsGroups: parsedGroups || [] };
  });

  yield put(changeArticlesLoadingStatus(false));
  yield put(changeArticles(articlesWithGroups));
  yield put(changeArticlesCount(articlesCount));
  yield put(changeSpotPolicy(spotPolicy));
}

function* setArticlesConversationPolicySaga(
  action: PayloadAction<{
    conversationId: string;
    policy: SpotPolicy;
  }>
) {
  const { conversationId, policy } = action.payload;
  const articles = yield select(selectArticlesList);
  const updatedArticles = articles.map(article =>
    article.conversationId === conversationId ? { ...article, policy } : article
  );

  yield call(handleApiCall, setArticlesConversationPolicyApiCall({ conversationId, policy }));
  yield put(changeArticles(updatedArticles));
}

const bulkActionToastMap: Record<BulkAction, string> = {
  'approve-all': 'The approval process for all pending comments for the selected article has started.',
  'archive-all': 'The processing of all pending comments for the selected article has started.',
  'reject-all': 'The rejection process for all pending comments for the selected article has started.'
};

function* applyBulkActionSaga(action: PayloadAction<BulkActionPayload>) {
  const { bulkAction, title, postId } = action.payload;
  try {
    yield call(postBulkAction, bulkAction, postId);

    yield put(stopBulkActionsStatusPolling());
    yield put(addRunningBulkAction({ postId, action: bulkAction }));
    yield put(startBulkActionsStatusPolling());
    yield put(
      showToast({
        type: 'info',
        message: bulkActionToastMap[bulkAction]
      })
    );
  } catch (error) {
    const message =
      error?.status === EXCEED_RATE_LIMIT
        ? 'This article is currently being processed by another team member. Please choose a different article.'
        : getToastErrorMessage(bulkAction, title);

    yield put(
      showToast({
        type: 'error',
        message
      })
    );
  }
}

const POLLING_DELAY = 60 * 1000;

export function* bulkActionsStatusWatchWorker() {
  yield delay(POLLING_DELAY);

  yield race({
    task: call(statusPollingWorker),
    cancel: take(stopBulkActionsStatusPolling.type)
  });
}

function* handleBulkResult(
  bulkActions: Array<BulkActionResponseResultWithId>,
  type: ToastType,
  messageFn: (action: BulkAction, title: string) => string,
  message: string
) {
  if (bulkActions.length === 0) {
    return;
  }

  const { title, jobType } = bulkActions[0];

  if (jobType !== null) {
    const finalMessage = bulkActions.length > 1 ? message : messageFn(jobType, title);

    yield put(
      showToast({
        type,
        message: finalMessage
      })
    );
  }
}

function* statusPollingWorker() {
  while (true) {
    try {
      const postIds: Array<string> = yield select(selectArticlesPostIdsForPolling);

      if (postIds.length === 0) {
        yield put(stopBulkActionsStatusPolling());
      } else {
        const { results }: GetBulkActionsStatusResponse = yield call(fetchBulkActionsStatus, postIds);
        const runningBulkActions = yield select(selectArticleRunningBulkActions);

        const finishedBulkActions: Array<string> = [];
        const errorBulkActions: Array<BulkActionResponseResultWithId> = [];
        const successBulkActions: Array<BulkActionResponseResultWithId> = [];

        Object.entries(results).forEach(([postId, result]) => {
          const { status, jobType } = result;
          const action = jobType || runningBulkActions.find(({ postId: id }) => id === postId)?.action;

          if (status !== 'running') {
            finishedBulkActions.push(postId);
          }

          const normalizedAction = { postId, ...result, jobType: action };

          if (status === 'error') {
            errorBulkActions.push(normalizedAction);
          }

          if (status === 'available') {
            successBulkActions.push(normalizedAction);
          }
        });

        yield put(removeRunningBulkActions(finishedBulkActions));

        yield* handleBulkResult(
          errorBulkActions,
          'error',
          getToastErrorMessage,
          `An error occurred while processing ${errorBulkActions} articles. Please try again later.`
        );

        yield* handleBulkResult(
          successBulkActions,
          'success',
          getToastSuccessMessage,
          `The processing of all pending comments for ${successBulkActions.length} articles is done.`
        );

        const continuePolling = postIds.length > finishedBulkActions.length;

        if (continuePolling) {
          yield delay(POLLING_DELAY);
        } else {
          yield put(stopBulkActionsStatusPolling());
        }
      }
    } catch (error) {
      yield delay(POLLING_DELAY);
      console.error(error);
    }
  }
}

function* toggleReadOnly(action) {
  try {
    const { readOnly, conversationId, postId } = action.payload;
    yield call(handleApiCall, setIsReadOnly(!readOnly, conversationId, postId));
    yield put(
      showToast({
        type: 'success',
        message: `Conversation ${readOnly ? 'opened' : 'closed'} successfully`
      })
    );
  } catch (error) {
    yield put(
      showToast({
        type: 'error',
        message: 'Conversation could not be closed/opened.'
      })
    );
  }
}

function* checkRunningBulkActionsSaga() {
  try {
    const { jobs }: GetRunningBulkActionsResponse = yield call(getRunningBulkActionsRequests);

    const runningBulkActions =
      jobs?.map(({ postId, jobType }) => ({
        postId,
        action: jobType
      })) || [];

    yield put(setRunningBulkActions(runningBulkActions));

    if (runningBulkActions.length > 0) {
      yield put(startBulkActionsStatusPolling());
    }
  } catch (error) {
    console.error(error);
  }
}

export function* watchToggleReadOnlySaga() {
  yield takeLatest(setReadOnlyAction, toggleReadOnly);
}

export function* loadArticlesSagas() {
  yield takeLatest(
    [
      initializeArticles, // this action could have separated handler @amudrenok
      changeArticlesCurrentPage,
      changeArticlesSearchValue,
      changeArticlesAppliedFilterTags,
      changeArticlesSortBy
    ],
    loadArticlesSaga
  );

  yield takeLatest(fetchArticlesFilterTags, loadFilterTagsSaga);
  yield takeLatest(setArticlesConversationPolicy, setArticlesConversationPolicySaga);
  yield takeLatest(applyBulkAction, applyBulkActionSaga);
  yield takeLatest(startBulkActionsStatusPolling, bulkActionsStatusWatchWorker);
  yield takeLatest(checkRunningBulkActions, checkRunningBulkActionsSaga);
}
